import { BUDGET_FRACTION } from '@orn/global/assistant/promptBudget.js';

import type { IMopsPromptBudgetArmV1 } from '@orn/global/assistant/promptBudget.js';

/**
 * Pure view helpers for the prompt-budget section of a benchmark verdict. They
 * mirror the PR comment's `render.ts` — same module-union walk, zero-collapse,
 * over-budget math, and headline logic — but return plain data the scene maps to
 * MOPS tables, and speak base/head (the tool's vocabulary) rather than
 * main/branch. All display math lives here so the JSX stays a thin map.
 */

/** The soft ceiling as a display percentage, e.g. `20%`. */
export const BUDGET_FRACTION_LABEL = `${Math.round(BUDGET_FRACTION * 100)}%`;

/** Localized integer for a token count. */
export function formatTokens(n: number): string {
  return n.toLocaleString('en-US');
}

/** Which way a metric moved base→head. More tokens is a regression (`increase`). */
export type DeltaTone = 'increase' | 'decrease' | 'flat' | 'none';

export interface IDeltaCell {
  /** Signed head−base delta, or null when there is no base to compare against. */
  delta: number | null;
  tone: DeltaTone;
  /** `+1,234` / `-1,234`, or `—` when unchanged or uncomparable. */
  label: string;
}

/**
 * Compare a head metric against its base counterpart. An `undefined` base (no
 * base arm, or a metric absent on the base side that shouldn't count as zero)
 * yields a no-comparison em dash; an exact tie also renders as an em dash but is
 * distinguished by `tone: 'flat'`.
 */
export function deltaCell(base: number | undefined, head: number): IDeltaCell {
  if (base === undefined) {
    return { delta: null, tone: 'none', label: '—' };
  }
  const d = head - base;
  if (d === 0) {
    return { delta: 0, tone: 'flat', label: '—' };
  }
  return { delta: d, tone: d > 0 ? 'increase' : 'decrease', label: `${d > 0 ? '+' : ''}${formatTokens(d)}` };
}

/** One base | head | Δ row of a prompt-budget table. */
export interface IMetricRow {
  label: string;
  /** null renders as `—` (no base arm). */
  base: number | null;
  head: number;
  delta: IDeltaCell;
  /** Bold the row (the section total). */
  emphasis?: boolean;
  /** Indent the row (a parameter nested under Parameters). */
  indent?: boolean;
}

function metricRow(
  label: string,
  base: number | undefined,
  head: number,
  opts: { emphasis?: boolean; indent?: boolean } = {}
): IMetricRow {
  return { label, base: base ?? null, head, delta: deltaCell(base, head), ...opts };
}

export interface IChannelBit {
  channel: string;
  tokens: number;
  delta: IDeltaCell;
}

export interface ISystemPromptTable {
  totalRow: IMetricRow;
  /** Per-module rows, in head order then base-only modules; both-zero modules excluded. */
  moduleRows: IMetricRow[];
  /** Modules empty on both arms, collapsed into one footnote row (empty when none). */
  zeroModules: string[];
  /** Per-channel assembled totals — catches channel-gated prose invisible in the module list. */
  channelBits: IChannelBit[];
}

const sectionsByName = (sections: readonly { name: string; tokens: number }[]): Map<string, number> =>
  new Map(sections.map((s) => [s.name, s.tokens]));

/**
 * The System-prompt table: the primary-channel total, then a per-module union of
 * base + head so an added module shows its full gain and a removed one its loss
 * (a side that's missing counts as 0 whenever there is a base). Modules empty on
 * both arms collapse into `zeroModules` so they don't drown the signal.
 */
export function systemPromptTable(
  base: IMopsPromptBudgetArmV1 | null,
  head: IMopsPromptBudgetArmV1
): ISystemPromptTable {
  const baseSections = base ? sectionsByName(base.sections) : undefined;
  const headSections = sectionsByName(head.sections);

  const orderedNames = [
    ...head.sections.map((s) => s.name),
    ...(base ? base.sections.map((s) => s.name).filter((n) => !headSections.has(n)) : []),
  ];

  const moduleRows: IMetricRow[] = [];
  const zeroModules: string[] = [];
  for (const name of orderedNames) {
    const headTok = headSections.get(name) ?? 0;
    const baseTok = base ? (baseSections?.get(name) ?? 0) : undefined;
    if (headTok === 0 && (baseTok ?? 0) === 0) {
      zeroModules.push(name);
      continue;
    }
    moduleRows.push(metricRow(name, baseTok, headTok));
  }

  const baseByChannel = new Map((base?.byChannel ?? []).map((c) => [c.channel, c.tokens]));
  const channelBits: IChannelBit[] = head.byChannel.map((c) => ({
    channel: c.channel,
    tokens: c.tokens,
    delta: deltaCell(baseByChannel.get(c.channel), c.tokens),
  }));

  return {
    totalRow: metricRow(`Total (${head.primaryChannel})`, base?.systemPromptTotalTokens, head.systemPromptTotalTokens, {
      emphasis: true,
    }),
    moduleRows,
    zeroModules,
    channelBits,
  };
}

export interface IToolTable {
  totalRow: IMetricRow;
  countRow: IMetricRow;
  descriptionsRow: IMetricRow;
  parametersRow: IMetricRow;
  /** The heaviest parameters (top 5), each rolled up across every tool that carries it. */
  topParamRows: IMetricRow[];
}

/** How many of the heaviest parameters the tool table lists. */
export const MAX_TOP_PARAMS = 5;

/** The Tool-registry table: registry totals plus the heaviest parameters nested under Parameters. */
export function toolTable(base: IMopsPromptBudgetArmV1 | null, head: IMopsPromptBudgetArmV1): IToolTable {
  const b = base?.tools;
  const h = head.tools;
  const baseParamTokens = new Map((b?.topParams ?? []).map((p) => [p.name, p.tokens]));

  return {
    totalRow: metricRow('Total tokens', b?.totalTokens, h.totalTokens, { emphasis: true }),
    countRow: metricRow('Tool count', b?.toolCount, h.toolCount),
    descriptionsRow: metricRow('Descriptions', b?.descriptionTokens, h.descriptionTokens),
    parametersRow: metricRow('Parameters', b?.parameterTokens, h.parameterTokens),
    topParamRows: h.topParams.slice(0, MAX_TOP_PARAMS).map((p) => {
      // A param absent on the base side counts as 0 (its full cost shows) when there is a
      // base at all, but stays undefined with no base — an uncomparable em dash, not a gain.
      const baseTok = b ? (baseParamTokens.get(p.name) ?? 0) : undefined;
      return metricRow(`${p.name} ×${p.toolCount}`, baseTok, p.tokens, { indent: true });
    }),
  };
}

export interface IBudgetSummary {
  model: string;
  estimator: string;
  windowKnown: boolean;
  contextWindowTokens: number;
  combinedTokens: number;
  /** The soft target in tokens, or null when the window is unknown. */
  budgetTokens: number | null;
  overBudget: boolean;
  /** Combined as a percentage of context, or null when the window is unknown. */
  pctOfContext: number | null;
  /** Combined static prefix, head vs base. */
  combinedDelta: IDeltaCell;
}

/** The model + budget headline numbers, degrading gracefully when the context window is unknown. */
export function budgetSummary(base: IMopsPromptBudgetArmV1 | null, head: IMopsPromptBudgetArmV1): IBudgetSummary {
  const windowKnown = head.contextWindowTokens > 0;
  const budgetTokens = windowKnown ? Math.round(head.contextWindowTokens * BUDGET_FRACTION) : null;
  return {
    model: head.model,
    estimator: head.estimator,
    windowKnown,
    contextWindowTokens: head.contextWindowTokens,
    combinedTokens: head.combinedTokens,
    budgetTokens,
    overBudget: budgetTokens !== null && head.combinedTokens > budgetTokens,
    pctOfContext: windowKnown ? (head.combinedTokens / head.contextWindowTokens) * 100 : null,
    combinedDelta: deltaCell(base?.combinedTokens, head.combinedTokens),
  };
}

/** Semantic tone for the headline verdict; the scene maps it to a theme text token. */
export type BudgetVerdictTone = 'ok' | 'warn' | 'over' | 'neutral';

export interface IBudgetVerdict {
  tone: BudgetVerdictTone;
  text: string;
}

/**
 * One-line headline mirroring the PR comment's: a regression (this run grew the
 * prefix) is the primary signal; being over the soft target is secondary state
 * that may pre-date the run, so the two are distinguished. When the context
 * window is unknown the budget can't be judged, so it never claims within/over.
 */
export function budgetVerdict(base: IMopsPromptBudgetArmV1 | null, head: IMopsPromptBudgetArmV1): IBudgetVerdict {
  const { windowKnown, overBudget, combinedDelta } = budgetSummary(base, head);
  const delta = base === null ? 0 : head.combinedTokens - base.combinedTokens;
  const grew = combinedDelta.label; // signed, localized

  if (!windowKnown) {
    if (delta > 0) {
      return { tone: 'warn', text: `grew ${grew} tok (model window unknown — budget not checked)` };
    }
    if (base === null) {
      return { tone: 'neutral', text: 'snapshot (no base comparison)' };
    }
    return {
      tone: 'neutral',
      text:
        delta < 0
          ? `shrank ${formatTokens(-delta)} tok (model window unknown)`
          : 'no regression (model window unknown — budget not checked)',
    };
  }

  if (delta > 0) {
    return overBudget
      ? { tone: 'over', text: `grew ${grew} tok, now over the ${BUDGET_FRACTION_LABEL} target` }
      : { tone: 'warn', text: `grew ${grew} tok (within target)` };
  }
  if (base === null) {
    return overBudget
      ? { tone: 'over', text: `over the ${BUDGET_FRACTION_LABEL} target (snapshot)` }
      : { tone: 'neutral', text: 'snapshot (no base comparison)' };
  }
  if (overBudget) {
    // Still over the soft target and this run didn't grow it — but a shrink is real
    // progress, so don't flatten `delta < 0` into "unchanged".
    return delta < 0
      ? { tone: 'warn', text: `over the ${BUDGET_FRACTION_LABEL} target, shrank ${formatTokens(-delta)} tok` }
      : { tone: 'warn', text: `over the ${BUDGET_FRACTION_LABEL} target (unchanged by this run)` };
  }
  return {
    tone: 'ok',
    text: delta < 0 ? `within target, shrank ${formatTokens(-delta)} tok` : 'within target, no regression',
  };
}
